import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TranslateService {
  constructor() {}

  /**
   * Translates a string ID against the runtime translation catalogue
   * (`window.GLOBAL_TRANSLATIONS`).
   *
   * `window.GLOBAL_TRANSLATIONS` is populated at app init from
   * `documents/translation/general` (see app.initializers.ts, which sets it
   * from the SAME object it passes to `loadTranslations()`), and is kept in
   * sync with per-service overrides by EregistrationsTranslationService. It is
   * the single synchronous source of truth the app already uses elsewhere
   * (the Formio `translate()` shim in index.html, form components, etc.).
   *
   * HISTORY: this lookup previously went through a *dynamic* `$localize` call
   * (`$localize({0: ':@@<id>:<id>', raw: [':']})`). That cannot be statically
   * analysed by `ng extract-i18n`, which aborted with "Unexpected messageParts
   * for $localize" and forced `src/assets/i18n/messages.json` to be
   * hand-maintained. Reading the catalogue directly removes that footgun and
   * re-enables extraction. Because this is a plain object lookup it also no
   * longer needs the colon-escaping workaround the `$localize` path required
   * (the ghost-numbers class of bug, TOBE-12725, cannot occur here).
   *
   * @param id - The string to translate (may contain colons or other special characters)
   * @returns Translated string, or the original string if no translation found
   * @see TOBE-17891 - Re-enable ng extract-i18n (this change)
   * @see TOBE-12725 - Original ghost numbers bug (colons in $localize)
   * @see TOBE-16599 - Centralized translation feature
   */
  localizeId(id?: any): string {
    if (!id) {
      return $localize`:@@none:none` as string;
    }

    if (typeof id !== 'string') {
      return this.resolveNonStringInput(id);
    }

    const match = this.tryTranslationWithFallback(id);
    return match === null ? id : match;
  }

  private resolveNonStringInput(id: any): string {
    if (id.message) return this.localizeId(id.message);
    if (id.detail) {
      return this.localizeId(
        typeof id.detail === 'string' ? id.detail : JSON.stringify(id.detail)
      );
    }
    if (typeof id === 'object') {
      const strings = this.extractNestedStrings(id);
      if (strings.length > 0) return strings.join(', ');
    }
    return String(id);
  }

  /**
   * Looks the id up in window.GLOBAL_TRANSLATIONS, trying the exact id first
   * then upper- and lower-case variants (case-insensitive fallback, preserved
   * from the previous implementation).
   *
   * @returns the translated string, or null if no translation found
   */
  private tryTranslationWithFallback(id: string): string | null {
    const translations = window.GLOBAL_TRANSLATIONS ?? {};
    const tryKey = (key: string): string | null => {
      // Own-property check only — never resolve inherited Object.prototype
      // members (e.g. "toString", "constructor") as translations.
      if (!Object.prototype.hasOwnProperty.call(translations, key)) return null;
      // eslint-disable-next-line security/detect-object-injection -- guarded own-property read from a plain string dictionary
      return translations[key];
    };

    return tryKey(id) ?? tryKey(id.toUpperCase()) ?? tryKey(id.toLowerCase());
  }

  private extractNestedStrings(obj: unknown): string[] {
    if (typeof obj === 'string') return [obj];
    if (Array.isArray(obj))
      return obj.flatMap((item) => this.extractNestedStrings(item));
    if (obj && typeof obj === 'object') {
      return Object.values(obj).flatMap((val) =>
        this.extractNestedStrings(val)
      );
    }
    return [];
  }
}
